import type { ErrorComponentProps } from "@tanstack/react-router";
// ported from index.html + App.tsx during the TanStack Start migration
import { useEffect, useState } from "react";
import {
  clearHardReloadFlag,
  hardReloadWithCacheBust,
  hasAlreadyHardReloaded,
  installStaleChunkRecovery,
  isStaleChunkError,
} from "@/lib/stale-chunk";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { captureAttribution } from "@/lib/attribution";
import NotFound from "@/pages/NotFound";
import appCss from "../styles.css?url";
import { PHONE_E164 } from "@/config/business";
import { MetaPixelTracker } from "@/components/MetaPixelTracker";
import {
  META_PIXEL_BOOTSTRAP,
  META_PIXEL_NOSCRIPT_SRC,
} from "@/lib/meta-pixel";

const ORGANIZATION_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://modernfenceinstall.com/#organization",
  name: "Modern Fence Install LLC",
  alternateName: "Modern Fence Install",
  url: "https://modernfenceinstall.com",
  logo: "https://modernfenceinstall.com/logo.png",
  image: "https://modernfenceinstall.com/og-default.jpg",
  description:
    "Professional non-electric containment fence installation for dogs, deer, and gardens — nearly invisible and engineered, with your install timeline confirmed in a written proposal.",
  telephone: PHONE_E164,
  email: "info@modernfenceinstall.com",
  // Nationwide installation eligibility remains governed by state install rules.
  areaServed: { "@type": "Country", name: "United States" },
});

const WEBSITE_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://modernfenceinstall.com/#website",
  url: "https://modernfenceinstall.com",
  name: "Modern Fence Install",
  publisher: { "@id": "https://modernfenceinstall.com/#organization" },
  inLanguage: "en-US",
});

const GA_MEASUREMENT_ID = import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"] ?? "G-4959PFJEQZ";

const GA_INLINE = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${GA_MEASUREMENT_ID}', {
  anonymize_ip: true,
  send_page_view: true
});`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "theme-color", content: "#1a1a1a" },
      { name: "author", content: "Modern Fence Install LLC" },
      {
        name: "keywords",
        content:
          "non-electric dog fence, invisible dog fence alternative, deer fence installation, garden fence installer, nearly invisible fence, professional fence installation",
      },
      { name: "google-site-verification", content: "nMr0WOP1u13K3AWGuvG31k2XuRI_AdpWGV6UgleQ_3s" },
      { name: "msvalidate.01", content: "345C486FFB0A731338E5F8061654B647" },
      { property: "og:site_name", content: "Modern Fence Install" },
      { property: "og:locale", content: "en_US" },
      // NOTE: title / description / og:title / og:description / og:image are
      // intentionally NOT set here. Each page renders its own via <SEO> (React 19
      // hoists them into <head> during SSR); a root copy would emit a duplicate
      // <title> that Google prefers over the real page title.
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },

    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "preconnect", href: "https://drjvpvgiivcdhevrvqvs.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://drjvpvgiivcdhevrvqvs.supabase.co" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
      },
    ],
    scripts: [
      { src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`, async: true },
      { children: GA_INLINE },
      { children: META_PIXEL_BOOTSTRAP },
      { type: "application/ld+json", children: ORGANIZATION_JSON_LD },
      { type: "application/ld+json", children: WEBSITE_JSON_LD },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src={META_PIXEL_NOSCRIPT_SRC}
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // ported from App.tsx: first-touch attribution — record UTMs / referrer /
  // landing page on first load (browser-only; SSR-safe guard inside).
  useEffect(() => {
    captureAttribution();
  }, []);

  // After a republish an open tab can still hold the old HTML shell, whose script
  // chunks 404/500. Auto-recover once with a cache-busting reload.
  useEffect(() => installStaleChunkRecovery(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <MetaPixelTracker />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  const stale = isStaleChunkError(error);
  const [autoRetrying, setAutoRetrying] = useState(false);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  // Stale shell after a deploy: reload once with a cache-busting param so the
  // browser can't serve the outdated script references again.
  useEffect(() => {
    if (!stale || hasAlreadyHardReloaded()) return;
    setAutoRetrying(true);
    const timer = window.setTimeout(() => hardReloadWithCacheBust(), 600);
    return () => window.clearTimeout(timer);
  }, [stale]);

  return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        {stale ? (
          <p className="text-muted-foreground">
            {autoRetrying
              ? "A newer version of the site was just released. Reloading with the latest files…"
              : "Your browser is holding an outdated copy of the site. Do a hard refresh to pick up the newest version — on desktop press Ctrl+Shift+R (Cmd+Shift+R on Mac); on mobile pull down to refresh or reopen the tab."}
          </p>
        ) : (
          <p className="text-muted-foreground">
            Something went wrong on our end. Try again, or do a hard refresh (Ctrl+Shift+R / Cmd+Shift+R) if it keeps
            happening.
          </p>
        )}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              if (stale) {
                clearHardReloadFlag();
                hardReloadWithCacheBust();
                return;
              }
              router.invalidate();
              reset();
            }}
          >
            {stale ? "Reload latest version" : "Try again"}
          </button>
          <button
            className="px-4 py-2 rounded-md border border-border bg-background text-foreground"
            onClick={() => {
              clearHardReloadFlag();
              hardReloadWithCacheBust();
            }}
          >
            Hard refresh
          </button>
          <a href="/" className="px-4 py-2 rounded-md border border-border bg-background text-foreground">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
